import produce from 'immer';
import { TravelType, WidgetTab } from '../../common';
import { cancelFlightAvailability, getFlightAvailability, Response, Status } from '../../services';
import { changed } from '../../utils';
import { amDeepLink, bookingFlowDeepLink } from '../../utils/deeplinkUtils';
import { BookingState } from '../state';
import { selectionChanged } from '../stateUtils';
import { SideEffect, SideEffectGenerator, Store } from '../types';
import { writePreviousSearchesToStore } from '../../utils/storeUtils';
import { bindActions } from 'redux-zero/utils';

export const getFlightAvailabilityEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getFlightAvailabilityStart: (state) =>
        produce(state, ({ ux }: BookingState) => {
          ux.loading.getFlightAvailability = true;
        }),
      _getFlightAvailabilityFinish: (state, { status, payload: flightsAvailable }: Response<boolean>) => {
        const nextState = produce(state, ({ ux }: BookingState) => {
          ux.loading.getFlightAvailability = false;
          ux.proceedToBooking = false;

          if (status !== Status.CANCELED) {
            ux.warnings.noFlights = true;
          }
        });

        const {
          ux,
          selection,
          currentTab,
          envConfig,
          amState: { amSelection },
          extensionApi: { navigator },
        } = nextState;

        if (flightsAvailable || status === Status.ERROR) {
          if (currentTab === WidgetTab.AM) {
            window.location.href = amDeepLink(amSelection, envConfig);
          } else {
            // @todo add support for multi city to the previous searches
            if (ux.previousSearchesEnabled && selection.travelType !== TravelType.MULTI_CITY) {
              writePreviousSearchesToStore(selection);
            }
            const instantSearchPrice = state.price.startingFrom?.amount;
            navigator.navigateToBooking(bookingFlowDeepLink(envConfig, selection, currentTab === WidgetTab.AWARD), {
              instantSearchPrice,
            });
          }
        }

        return nextState;
      },
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        selection,
        location: { fppEnabled },
        extensionApi: { getLoginToken },
      } = readOnlyState;

      effectActions._getFlightAvailabilityStart();
      getFlightAvailability(envConfig, selection, fppEnabled, getLoginToken).then(
        ({ status, payload: flightsAvailable }: Response<boolean>) => {
          effectActions._getFlightAvailabilityFinish({ status, payload: flightsAvailable });
        }
      );
    },
    when: (prev: BookingState, next: BookingState) => {
      const isAward = next.currentTab === WidgetTab.AWARD;
      const isFlexible = isAward || next.selection.travelDates.flexible;

      return changed(prev, next, 'ux', 'proceedToBooking') && next.ux.proceedToBooking && !isFlexible;
    },

    name: getFlightAvailabilityEffect.name,
  };
};

export const clearFlightAvailabilityWarningEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _clearFlightAvailabilityWarning: (state) => {
        cancelFlightAvailability();
        return produce(state, ({ ux }: BookingState) => {
          ux.warnings.noFlights = false;
        });
      },
    },
    store
  );

  return {
    runSideEffects: effectActions._clearFlightAvailabilityWarning,
    when: (prev, next) => selectionChanged(prev, next) && next.ux.loading.getFlightAvailability,
    name: clearFlightAvailabilityWarningEffect.name,
  };
};

export const clearNoFlightsWarningEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _clearNoFlightsWarning: (state) =>
        produce(state, ({ ux }: BookingState) => {
          ux.warnings.noFlights = false;
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._clearNoFlightsWarning,
    when: (prev, next) => selectionChanged(prev, next) && next.ux.warnings.noFlights,
    name: clearNoFlightsWarningEffect.name,
  };
};

export const bookingEffects: SideEffectGenerator<BookingState>[] = [
  getFlightAvailabilityEffect,
  clearFlightAvailabilityWarningEffect,
  clearNoFlightsWarningEffect,
];
